/* import __COLOCATED_TEMPLATE__ from './schedule-availability-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import { post, put, ajaxDelete } from 'embercom/lib/ajax';
import type { ScheduleRecord } from './fin-section';
import type CustomReportsService from 'embercom/services/custom-reports-service';

interface AgentOption {
  label: string;
  key: string;
}

interface Args {
  onClose: () => void;
  agentOptions: AgentOption[];
  entityId: number;
  entityType: number;
  onScheduleCreated: () => void;
  existingSchedule?: ScheduleRecord;
}

interface Signature {
  Args: Args;
}

export default class ScheduleAvailabilityModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  @tracked selectedTimezone: string = this.defaultTimezone;
  @tracked selectedAgentKeys: Set<string> = new Set(
    this.args.existingSchedule
      ? this.args.existingSchedule.agents
      : this.args.agentOptions.map((opt) => opt.key),
  );
  @tracked selectedAction: 'enable' | 'disable' =
    this.args.existingSchedule?.transition_type ?? 'enable';
  @tracked scheduledDate = this.initialScheduledDate;
  @tracked showEndDate = !!this.args.existingSchedule?.end_at;
  @tracked endDate = this.initialEndDate;
  @tracked isSaving = false;

  get defaultTimezone(): string {
    return this.args.existingSchedule?.timezone ?? this.appService.app.timezone;
  }

  private get initialScheduledDate(): Date {
    if (this.args.existingSchedule) {
      // Date holds the real UTC instant — the picker is told to display it in
      // selectedTimezone via the @timezone arg in the template.
      return new Date(this.args.existingSchedule.start_at);
    }
    return this.defaultScheduledDate;
  }

  private get initialEndDate(): Date {
    if (this.args.existingSchedule?.end_at) {
      return new Date(this.args.existingSchedule.end_at);
    }
    return this.defaultEndDate;
  }

  get timezoneOptions(): { text: string; value: string }[] {
    return this.customReportsService.timezones().map((tz) => ({
      text: tz.description,
      value: tz.description,
    }));
  }

  get selectedTimezoneDescription(): string {
    let tz = this.customReportsService.timezones().find((t) => t.value === this.selectedTimezone);
    return tz?.description ?? this.selectedTimezone;
  }

  // Re-anchor scheduledDate and endDate so each UTC instant means "same wall-clock,
  // new timezone". Without this, the Dates keep representing the old zone's
  // wall-clock and the future-date check fails when the user picks a westward
  // timezone (e.g. GMT+1 from GMT+6).
  @action onTimezoneChange(description: string) {
    let tz = this.customReportsService.timezones().find((t) => t.description === description);
    if (!tz) {
      return;
    }
    let oldTz = this.selectedTimezone;
    let startWallClock = moment(this.scheduledDate).tz(oldTz).format('YYYY-MM-DD HH:mm:ss');
    let endWallClock = moment(this.endDate).tz(oldTz).format('YYYY-MM-DD HH:mm:ss');
    this.selectedTimezone = tz.value;
    this.scheduledDate = moment.tz(startWallClock, 'YYYY-MM-DD HH:mm:ss', tz.value).toDate();
    this.endDate = moment.tz(endWallClock, 'YYYY-MM-DD HH:mm:ss', tz.value).toDate();
  }

  get today() {
    return new Date();
  }

  get agentSelectItems() {
    return this.args.agentOptions.map((opt) => ({
      value: opt.key,
      text: opt.label,
      isSelected: this.selectedAgentKeys.has(opt.key),
    }));
  }

  get selectedAgentsLabel() {
    let selected = this.args.agentOptions.filter((opt) => this.selectedAgentKeys.has(opt.key));
    if (selected.length === 0) {
      return this.intl.t(
        'knowledge-hub.content-editor.shared.schedule-availability-modal.select-ai-product',
      );
    }
    let labels = selected.map((opt) => opt.label);
    if (labels.length === 1) {
      return labels[0];
    }
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  get defaultScheduledDate() {
    return moment
      .tz(this.selectedTimezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get defaultEndDate() {
    return moment(this.scheduledDate).add(1, 'hour').second(0).millisecond(0).toDate();
  }

  @action onAgentSelectionChange(selectedValues: string[]) {
    this.selectedAgentKeys = new Set(selectedValues);
  }

  @action onActionChange(value: 'enable' | 'disable') {
    this.selectedAction = value;
  }

  @action onScheduledDateChange(date: Date) {
    this.scheduledDate = date;
  }

  @action onEndDateChange(date: Date) {
    this.endDate = date;
  }

  @action toggleEndDate() {
    this.showEndDate = !this.showEndDate;
  }

  get isEditing() {
    return !!this.args.existingSchedule;
  }

  get scheduledDateError() {
    if (this.scheduledDate <= new Date()) {
      return this.intl.t(
        'knowledge-hub.content-editor.shared.schedule-availability-modal.start-date-error',
      );
    }
    return null;
  }

  get endDateTooltip() {
    return this.selectedAction === 'enable'
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.schedule-availability-modal.end-date-tooltip-enable',
        )
      : this.intl.t(
          'knowledge-hub.content-editor.shared.schedule-availability-modal.end-date-tooltip-disable',
        );
  }

  get endDateError() {
    if (this.showEndDate && this.endDate <= this.scheduledDate) {
      return this.intl.t(
        'knowledge-hub.content-editor.shared.schedule-availability-modal.end-date-error',
      );
    }
    return null;
  }

  get canSave() {
    return (
      this.selectedAgentKeys.size > 0 &&
      !this.isSaving &&
      !this.scheduledDateError &&
      !this.endDateError
    );
  }

  @action async deleteSchedule() {
    this.isSaving = true;
    try {
      await ajaxDelete(
        `/ember/knowledge_hub/ai_availability_schedules/${this.args.existingSchedule!.group_id}?app_id=${this.appService.app.id}`,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'knowledge-hub.content-editor.shared.schedule-availability-modal.delete-success',
        ),
      );
      this.args.onScheduleCreated();
      this.args.onClose();
    } catch (_error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.shared.schedule-availability-modal.delete-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action async save() {
    if (this.selectedAgentKeys.size === 0) {
      return;
    }

    this.isSaving = true;
    try {
      let payload = {
        app_id: this.appService.app.id,
        entity_type: this.args.entityType,
        entity_id: this.args.entityId,
        agent_types: Array.from(this.selectedAgentKeys),
        availability_action: this.selectedAction,
        scheduled_at: this.scheduledDate.toISOString(),
        timezone: this.selectedTimezone,
        ...(this.showEndDate ? { end_date: this.endDate.toISOString() } : {}),
      };

      if (this.isEditing) {
        await put(
          `/ember/knowledge_hub/ai_availability_schedules/${this.args.existingSchedule!.group_id}`,
          payload,
        );
      } else {
        await post('/ember/knowledge_hub/ai_availability_schedules', payload);
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.content-editor.shared.schedule-availability-modal.save-success'),
      );
      this.args.onScheduleCreated();
      this.args.onClose();
    } catch (_error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.shared.schedule-availability-modal.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::ScheduleAvailabilityModal': typeof ScheduleAvailabilityModal;
    'knowledge-hub/content-editor/shared/schedule-availability-modal': typeof ScheduleAvailabilityModal;
  }
}
